import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  CheckCircle2, Ticket, ArrowRight, Clock, AlertTriangle, Compass, Users, User, Package,
} from 'lucide-react';
import { bfTrack } from '../utils/bfPixel';
import {
  getItinerary, getDone, clearDone, resolvePaid, payForItem, removeFromItinerary, msRemaining,
} from '../utils/itinerary';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import CustomerSignInModal from '../components/CustomerSignInModal';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Tokenized magic short-link — mints a secure ticket JWT server-side.
// Packages (PK-…) land on the package recap page which lists every child ticket.
const ticketUrl = (code) => (String(code || '').startsWith('PK-')
  ? `/package/confirm?pkg=${code}`
  : `${API}/api/v2/public/t/${code}`);

const fmtCountdown = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const TextLink = ({ to, href, onClick, color = 'blue', children, testId, className = '' }) => {
  const cls = `inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${
    color === 'green' ? 'text-emerald-600 hover:text-emerald-700' : 'text-blue-600 hover:text-blue-700'
  } ${className}`;
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children}<ArrowRight className="w-4 h-4" /></Link>;
  if (href) return <a href={href} className={cls} data-testid={testId}>{children}<ArrowRight className="w-4 h-4" /></a>;
  return <button onClick={onClick} className={cls} data-testid={testId}>{children}<ArrowRight className="w-4 h-4" /></button>;
};

/**
 * /cart/continue — the chained-payment hub. Flat, distilled, no borders.
 * Stripe success lands here with ?code=BM-XXXX, cancel with ?cancelled=1.
 */
export default function CartContinuePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { customer } = useCustomerAuth();
  const [items, setItems] = useState([]);
  const [done, setDone] = useState([]);
  const [justPaid, setJustPaid] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [showSignIn, setShowSignIn] = useState(false);
  const [, setTick] = useState(0);
  const resolved = useRef(false);

  useEffect(() => {
    if (resolved.current) return;
    resolved.current = true;
    const code = searchParams.get('code');
    if (code) {
      const paidItem = resolvePaid(code) || { code };
      setJustPaid(paidItem);
      // BookingFlows pixel — Purchase (deduped per confirmation code).
      // Value comes from the confirm-payment response (authoritative paid
      // amount) — Meta rejects Purchase events with missing/zero value.
      const pk = `bf_pix_purch_${code}`;
      const firePurchase = (rawValue, currency) => {
        try {
          if (localStorage.getItem(pk)) return;
          localStorage.setItem(pk, '1');
          bfTrack('Purchase', {
            value: Math.round((Number(rawValue) || 0) * 100) / 100,
            currency: currency || 'USD',
            content_ids: [code],
            content_type: 'product',
          }, `purchase_${code}`);
        } catch { /* noop */ }
      };
      // Settle payment against Stripe (primary path for direct charges —
      // this is what marks the booking confirmed + fires the ticket emails)
      if (code.startsWith('PK-')) {
        // Package: one shared session — settle every child booking
        fetch(`${API}/api/v2/public/package-bookings/${code}`)
          .then((r) => r.json())
          .then(async (d) => {
            for (const c of (d.children || [])) {
              if (!['paid', 'deposit_paid', 'completed'].includes(c.paymentStatus)) {
                try { await fetch(`${API}/api/v2/public/bookings/${c.code}/confirm-payment`, { method: 'POST' }); } catch { /* webhook settles */ }
              }
            }
            firePurchase(d?.totals?.total ?? paidItem.display?.total, d?.totals?.currency || paidItem.display?.currency);
          })
          .catch(() => { firePurchase(paidItem.display?.total, paidItem.display?.currency); });
      } else {
        fetch(`${API}/api/v2/public/bookings/${code}/confirm-payment`, { method: 'POST' })
          .then((r) => r.json())
          .then((d) => firePurchase(
            d?.conversionTracking?.value ?? d?.paidAmount ?? paidItem.display?.total,
            d?.conversionTracking?.currency || paidItem.display?.currency,
          ))
          .catch(() => { firePurchase(paidItem.display?.total, paidItem.display?.currency); /* webhook/cron will settle it */ });
      }
    }
    if (searchParams.get('cancelled') === '1') setCancelled(true);
    setItems(getItinerary());
    setDone(getDone());
  }, [searchParams]);

  useEffect(() => {
    const t = setInterval(() => { setTick((n) => n + 1); setItems(getItinerary()); }, 1000);
    return () => clearInterval(t);
  }, []);

  const payNext = async () => {
    if (!items.length || paying) return;
    setPaying(true);
    setError('');
    try {
      await payForItem(items[0]);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setPaying(false);
    }
  };

  const removeItem = (id) => {
    removeFromItinerary(id);
    setItems(getItinerary());
  };

  const totalCount = done.length + items.length;
  const allDone = items.length === 0;
  const otherDone = done.filter((d) => d.code !== justPaid?.code);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="cart-continue-page">
      <header className="w-full max-w-2xl mx-auto px-4 pt-6 pb-2 flex items-center justify-between">
        <button onClick={() => navigate('/marketplace')} className="flex items-center gap-2" data-testid="cart-continue-logo">
          <img src="/logo-icon.png" alt="BookingFlows" className="w-8 h-8 rounded-lg object-cover" />
          <span className="font-bold text-gray-900">BookingFlows</span>
        </button>
        <TextLink to="/my-trips" testId="cart-continue-mytrips-link">My Trips</TextLink>
      </header>

      <main className="w-full max-w-2xl mx-auto px-4 py-6 space-y-4 flex-1">
        {justPaid && (
          <div className="bg-emerald-50 rounded-2xl p-5 sm:p-6" data-testid="cart-paid-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-gray-900">{justPaid.code?.startsWith('PK-') ? 'Package confirmed!' : 'Booking confirmed!'}</h1>
                <p className="text-sm text-gray-600 mt-0.5">
                  {justPaid.serviceName ? <><span className="font-medium">{justPaid.serviceName}</span> · </> : null}
                  Confirmation <span className="font-mono font-semibold">{justPaid.code}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">{justPaid.code?.startsWith('PK-') ? 'One ticket per experience has been emailed to you — the link below shows them all.' : 'Your ticket has been emailed to you — the link below is your secure ticket access.'}</p>
                <div className="mt-3">
                  <TextLink href={ticketUrl(justPaid.code)} color="green" testId="cart-view-ticket-btn">
                    <Ticket className="w-4 h-4" /> {justPaid.code?.startsWith('PK-') ? 'View your tickets' : 'View your ticket'}
                  </TextLink>
                </div>
              </div>
            </div>
          </div>
        )}

        {cancelled && (
          <div className="bg-amber-50 rounded-2xl p-4 flex items-start gap-3" data-testid="cart-cancelled-notice">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Payment wasn't completed</p>
              <p className="text-xs text-amber-700 mt-0.5">No worries — your experience is still in your cart below. Try the payment again when you're ready.</p>
            </div>
          </div>
        )}

        {done.length > 0 && (
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5" data-testid="cart-done-list">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Ticket className="w-3.5 h-3.5" /> Your tickets from this session ({done.length})
            </p>
            {done.map((d) => (
              <div key={d.code} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-1.5">
                    {d.serviceName || 'Experience'}
                    {d.code === justPaid?.code && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5" data-testid="cart-done-just-confirmed">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Just confirmed
                      </span>
                    )}
                  </p>
                  <p className="text-xs font-mono text-gray-500">{d.code}</p>
                </div>
                <TextLink href={ticketUrl(d.code)} color="green" testId={`cart-done-ticket-${d.code}`}>View ticket</TextLink>
              </div>
            ))}
          </div>
        )}

        {!allDone && (
          <div className="bg-gray-50 rounded-2xl p-5 sm:p-6" data-testid="cart-remaining-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-gray-900">
                {done.length > 0 ? `Next up — ${done.length + 1} of ${totalCount}` : 'Ready to complete'}
              </h2>
              <span className="text-xs text-gray-500">{items.length} remaining</span>
            </div>

            <div className="space-y-2.5">
              {items.map((item, idx) => {
                const d = item.display || {};
                return (
                  <div key={item.id} className={`rounded-xl p-3 flex gap-3 ${idx === 0 ? 'bg-blue-50' : 'bg-white'}`} data-testid={`cart-continue-item-${idx}`}>
                    {d.image && <img src={d.image} alt={d.serviceName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {d.isPackage && <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 mr-1.5 align-middle"><Package className="w-2.5 h-2.5" /> Package</span>}
                        {d.serviceName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{d.orgName}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-600">
                        <span>{fmtDate(d.date)} · {d.displayTime}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{d.guests}</span>
                        <span className="flex items-center gap-1 text-amber-600 font-medium"><Clock className="w-3 h-3" />{fmtCountdown(msRemaining(item))}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between flex-shrink-0">
                      <span className="text-sm font-bold text-gray-900">{d.symbol}{Number(d.total || 0).toFixed(2)}</span>
                      <button onClick={() => removeItem(item.id)} className="text-[11px] text-gray-400 hover:text-red-500" data-testid={`cart-continue-remove-${idx}`}>Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {error && (
              <p data-testid="cart-continue-error" className="mt-3 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              onClick={payNext}
              disabled={paying}
              data-testid="cart-pay-next-btn"
              className="w-full mt-4 py-3.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {paying ? 'Starting secure checkout…' : (
                <>
                  Pay for {done.length > 0 ? 'your next experience' : 'this experience'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="text-[11px] text-gray-400 text-center mt-2">Paid securely to the operator via Stripe.</p>
          </div>
        )}

        {allDone && (done.length > 0 || justPaid) && (
          <div className="bg-gray-50 rounded-2xl p-5 text-center" data-testid="cart-all-done-card">
            <h2 className="text-base font-bold text-gray-900">
              {done.length > 1 ? `All ${done.length} experiences booked 🎉` : 'You\u2019re all set!'}
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">Every ticket has been emailed to you with its operator's contact and check-in details.</p>
            <div className="mt-3">
              <TextLink onClick={() => { clearDone(); navigate('/search'); }} testId="cart-keep-exploring-btn">
                <Compass className="w-4 h-4" /> Keep exploring experiences
              </TextLink>
            </div>
          </div>
        )}

        {allDone && done.length === 0 && !justPaid && (
          <div className="bg-gray-50 rounded-2xl p-6 text-center" data-testid="cart-empty-card">
            <p className="text-sm text-gray-600">Your cart is empty (items are held for 10 minutes).</p>
            <div className="mt-4 flex justify-center">
              <TextLink to="/search" testId="cart-empty-browse-link">
                <Compass className="w-4 h-4" /> Browse experiences
              </TextLink>
            </div>
          </div>
        )}

        {/* Account — distilled, single block (support lives in the footer) */}
        <div className="bg-blue-50 rounded-2xl p-4 flex items-center gap-3" data-testid="cart-account-block">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <User className="w-4.5 h-4.5 text-blue-600" style={{ width: 18, height: 18 }} />
          </div>
          <div className="min-w-0">
            {customer ? (
              <>
                <p className="text-xs text-gray-500 truncate">Signed in as {customer.name?.split(' ')[0] || customer.email}</p>
                <TextLink to="/my-trips" testId="cart-account-profile" className="mt-0.5">My Profile</TextLink>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500">Your bookings, one account</p>
                <TextLink onClick={() => setShowSignIn(true)} testId="cart-account-signin" className="mt-0.5">Sign in</TextLink>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Slim footer */}
      <footer className="w-full max-w-2xl mx-auto px-4 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-400" data-testid="cart-continue-footer">
        <Link to="/support" className="hover:text-gray-600 transition-colors">Support Center</Link>
        <Link to="/find-booking" className="hover:text-gray-600 transition-colors" data-testid="cart-footer-lookup">Look up a booking</Link>
        <Link to="/search" className="hover:text-gray-600 transition-colors">Explore experiences</Link>
      </footer>

      <CustomerSignInModal open={showSignIn} onOpenChange={setShowSignIn} />
    </div>
  );
}
